/**
 * Rule Build Chat Streaming API
 *
 * RTK Query API with fakeBaseQuery for SSE streaming using @microsoft/fetch-event-source
 * Handles streaming communication with the rule build chat backend (AG-UI Protocol).
 */

import type { AGUISSEEvent, SSEPerformanceMetrics, StepPayload } from '@features/ai-chat-core';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';
import { Mutex } from 'async-mutex';

import { ruleBuildApi } from './api';
import {
  appendEntityContent,
  appendStreamingContent,
  clearAllEntityUpdates,
  clearInterrupt,
  completeWorkflow,
  finalizeEntityStreaming,
  finalizeMessage,
  markEntityAffected,
  type RuleBuildChatState,
  setActivitySnapshot,
  setAssistantStatus,
  setCurrentStep,
  setError,
  setFocusedDraftId,
  setSSEPerformanceMetrics,
  setThreadId,
  showInterrupt,
  startEntityStreaming,
  startStreamingMessage,
} from './chatSlice';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// ============================================================================
// Types
// ============================================================================

/**
 * Request payload for rule build chat
 */
export interface RuleBuildChatRequest {
  taskId: string;
  threadId?: string;
  runId: string;
  messages?: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
  }>;
  resume?: {
    interruptId: string;
    payload: unknown;
  };
  focusedRuleId?: string;
}

/**
 * Streaming cache data
 */
export interface RuleBuildChatStreamData {
  isActive: boolean;
  isDone: boolean;
}

/**
 * Extended SSE Event type for rule build chat
 */
type RuleBuildSSEEvent =
  | AGUISSEEvent
  | {
      type:
        | 'ENTITY_UPDATE_START'
        | 'ENTITY_UPDATE_CONTENT'
        | 'ENTITY_UPDATE_END'
        | 'INTERRUPT'
        | 'ENTITY_AFFECTED'
        | 'FOCUS_DRAFT';
      entityId?: string;
      draftId?: string;
      content?: string;
      interrupt?: unknown;
      delta?: string;
      step_name?: string;
      messageId?: string;
      timestamp?: number;
      replace?: boolean;
    };

// ============================================================================
// Auth Token Management
// ============================================================================

const mutex = new Mutex();

// ============================================================================
// API Definition
// ============================================================================

export const ruleBuildStreamingApi = createApi({
  reducerPath: 'ruleBuild/streaming',
  baseQuery: fakeBaseQuery(),
  endpoints: builder => ({
    /**
     * Stream rule build chat messages with interrupt workflow (AG-UI Protocol)
     */
    streamRuleBuildChat: builder.query<RuleBuildChatStreamData, RuleBuildChatRequest>({
      queryFn: () => ({
        data: {
          isActive: false,
          isDone: false,
        },
      }),

      async onCacheEntryAdded(
        payload,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { updateCachedData, cacheEntryRemoved, dispatch, getState },
      ) {
        const controller = new AbortController();

        const taskId = payload.taskId;

        // Performance tracking
        const requestStartTime = Date.now();
        const runId = payload.runId;

        let connectionOpenTime: number | null = null;
        let firstEventTime: number | null = null;
        let firstTokenTime: number | null = null;
        let streamEndTime: number | null = null;
        let eventCount = 0;
        let outcome: 'success' | 'interrupt' | 'error' = 'success';
        let metricsDispatched = false;

        const thresholdMs = parseInt(
          import.meta.env.VITE_SSE_PERFORMANCE_THRESHOLD_MS || '10000',
          10,
        );

        const dispatchPerformanceMetrics = () => {
          if (metricsDispatched) return;
          if (!connectionOpenTime || !firstEventTime || !streamEndTime) return;

          metricsDispatched = true;

          const metrics: SSEPerformanceMetrics = {
            time_to_connection_ms: connectionOpenTime - requestStartTime,
            time_to_first_event_ms: firstEventTime - requestStartTime,
            time_to_first_token_ms: firstTokenTime
              ? firstTokenTime - requestStartTime
              : firstEventTime - requestStartTime,
            total_duration_ms: streamEndTime - requestStartTime,
            run_id: runId,
            thread_id: payload.threadId || undefined,
            outcome,
            event_count: eventCount,
            exceeded_threshold: streamEndTime - requestStartTime > thresholdMs,
            threshold_ms: thresholdMs,
          };

          dispatch(setSSEPerformanceMetrics(metrics));
        };

        await mutex.waitForUnlock();

        let token: string;
        try {
          token = await getAuth0AccessTokenSilently();
        } catch (error) {
          console.error('Failed to get access token for streaming:', error);
          dispatch(setError('Authentication failed. Please log in again.'));
          outcome = 'error';
          streamEndTime = Date.now();
          dispatchPerformanceMetrics();
          return;
        }

        // Internal map to track tool calls for logging
        const toolCallMap = new Map<string, { toolCallName: string; args: string }>();

        (async () => {
          try {
            await fetchEventSource(`${API_BASE_URL}/v2/provider/rules/build/${taskId}/chat`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload),
              signal: controller.signal,

              async onopen(res) {
                if (!res.ok) {
                  const errorText = await res.text();
                  if (res.status === 401) {
                    if (!mutex.isLocked()) {
                      const release = await mutex.acquire();
                      try {
                        dispatch(
                          setError('Session expired. Please refresh the page and log in again.'),
                        );
                      } finally {
                        release();
                      }
                    } else {
                      await mutex.waitForUnlock();
                      dispatch(setError('Session expired. Please try again or refresh the page.'));
                    }
                  } else {
                    dispatch(setError(`Failed to connect: ${res.status} ${res.statusText}`));
                  }

                  updateCachedData(draft => {
                    draft.isActive = false;
                  });

                  throw new Error(`Stream failed with status ${res.status}: ${errorText}`);
                }

                console.debug('📡 [SSE] Rule build chat connection opened', {
                  taskId,
                  threadId: payload.threadId,
                });

                if (!connectionOpenTime) {
                  connectionOpenTime = Date.now();
                }

                updateCachedData(draft => {
                  draft.isActive = true;
                  draft.isDone = false;
                });
              },

              onmessage: event => {
                if (!event.data) return;

                if (!firstEventTime) {
                  firstEventTime = Date.now();
                }
                eventCount++;

                let data: RuleBuildSSEEvent;
                try {
                  data = JSON.parse(event.data) as RuleBuildSSEEvent;
                } catch {
                  console.warn('Failed to parse SSE event:', event.data);
                  return;
                }

                console.debug('📡 [SSE] Rule build chat event received:', {
                  type: data.type,
                  keys: Object.keys(data),
                });

                switch (data.type) {
                  case 'RUN_STARTED': {
                    const state = getState() as Record<string, unknown>;
                    const chatState = state['ruleBuild/chat'] as RuleBuildChatState | undefined;
                    const currentThreadId = chatState?.threadId;
                    const newThreadId = 'threadId' in data ? data.threadId : null;
                    const isNewThread = newThreadId && newThreadId !== currentThreadId;

                    if (newThreadId) {
                      dispatch(setThreadId(newThreadId));
                    }
                    dispatch(setAssistantStatus('thinking'));

                    if (isNewThread) {
                      console.debug('📡 [AG-UI] New thread started, clearing entity updates');
                      dispatch(clearAllEntityUpdates());
                    }

                    const currentInterrupt = chatState?.interrupt;
                    if (currentInterrupt?.eventType === 'CONFIRMATION') {
                      console.debug('📡 [AG-UI] Clearing CONFIRMATION interrupt on RUN_STARTED');
                      dispatch(clearInterrupt());
                    }
                    break;
                  }

                  case 'STEP_STARTED': {
                    const stepName = 'step_name' in data ? data.step_name : '';
                    if (stepName) {
                      dispatch(setCurrentStep(stepName));
                    }
                    break;
                  }

                  case 'ACTIVITY_SNAPSHOT': {
                    const activityData = data as {
                      type: 'ACTIVITY_SNAPSHOT';
                      messageId: string;
                      activityType: string;
                      content: { message: string };
                      timestamp: number;
                      replace?: boolean;
                    };
                    dispatch(
                      setActivitySnapshot({
                        messageId: activityData.messageId,
                        activityType: activityData.activityType as 'THINKING' | 'PLAN' | 'SEARCH',
                        message: activityData.content.message,
                        timestamp: activityData.timestamp,
                        replace: activityData.replace,
                      }),
                    );
                    break;
                  }

                  case 'TEXT_MESSAGE_START': {
                    const messageId = 'messageId' in data ? data.messageId : crypto.randomUUID();
                    dispatch(startStreamingMessage(messageId || crypto.randomUUID()));
                    break;
                  }

                  case 'TEXT_MESSAGE_CONTENT': {
                    const delta = 'delta' in data ? data.delta : '';
                    if (delta) {
                      dispatch(appendStreamingContent(delta));
                      if (!firstTokenTime) {
                        firstTokenTime = Date.now();
                      }
                    }
                    break;
                  }

                  case 'TEXT_MESSAGE_END': {
                    dispatch(finalizeMessage());
                    break;
                  }

                  case 'ENTITY_UPDATE_START': {
                    const entityId = 'entityId' in data ? data.entityId : crypto.randomUUID();
                    if (entityId) {
                      dispatch(startEntityStreaming(entityId));
                    }
                    break;
                  }

                  case 'ENTITY_UPDATE_CONTENT': {
                    const entityId = (data as { entityId?: string }).entityId || '';
                    const content = (data as { content?: string }).content || '';
                    if (entityId && content) {
                      dispatch(appendEntityContent({ entityId, content }));
                    }
                    break;
                  }

                  case 'ENTITY_UPDATE_END': {
                    const entityId = 'entityId' in data ? data.entityId : crypto.randomUUID();
                    if (entityId) {
                      dispatch(finalizeEntityStreaming(entityId));
                    }
                    break;
                  }

                  case 'FOCUS_DRAFT': {
                    // Backend requests UI to focus on a specific draft
                    const draftId = (data as { draftId?: string }).draftId;
                    if (draftId) {
                      console.debug('📡 [SSE] FOCUS_DRAFT received:', { draftId });
                      dispatch(setFocusedDraftId(draftId));
                    }
                    break;
                  }

                  case 'INTERRUPT': {
                    const interruptData = (data as { interrupt?: unknown }).interrupt as {
                      id: string;
                      payload: {
                        eventType: 'CONFIRMATION' | 'MULTIPLE_CHOICE' | 'MULTI_STEP' | 'EXPRESS_INTEREST';
                        confirmLabel?: string;
                        cancelLabel?: string;
                        message?: string;
                        question?: string;
                        options?: Array<{ id: string; label: string; text?: string }>;
                        allowMultiple?: boolean;
                        steps?: StepPayload[];
                        metadata?: unknown;
                      };
                    };
                    if (interruptData) {
                      dispatch(
                        showInterrupt({
                          interruptId: interruptData.id,
                          eventType: interruptData.payload.eventType,
                          confirmLabel: interruptData.payload.confirmLabel,
                          cancelLabel: interruptData.payload.cancelLabel,
                          message: interruptData.payload.message,
                          question: interruptData.payload.question,
                          options: interruptData.payload.options,
                          allowMultiple: interruptData.payload.allowMultiple,
                          steps: interruptData.payload.steps,
                          metadata: interruptData.payload.metadata,
                        }),
                      );
                      outcome = 'interrupt';
                    }
                    break;
                  }

                  case 'RUN_FINISHED': {
                    const runFinishedData = data as {
                      type: 'RUN_FINISHED';
                      outcome?: 'success' | 'interrupt' | 'error';
                      interrupt?: {
                        id: string;
                        payload: {
                          eventType: 'CONFIRMATION' | 'MULTIPLE_CHOICE' | 'MULTI_STEP' | 'EXPRESS_INTEREST';
                          confirmLabel?: string;
                          cancelLabel?: string;
                          message?: string;
                          question?: string;
                          options?: Array<{ id: string; label: string; text?: string }>;
                          allowMultiple?: boolean;
                          namespace?: 'global' | 'chat';
                          confirmationType?: 'entity_changes' | 'train' | 'rule_save';
                          steps?: StepPayload[];
                          metadata?: unknown;
                        };
                      };
                    };

                    // Check if the run finished with an interrupt
                    if (runFinishedData.outcome === 'interrupt' && runFinishedData.interrupt) {
                      const interruptData = runFinishedData.interrupt;
                      dispatch(
                        showInterrupt({
                          interruptId: interruptData.id,
                          eventType: interruptData.payload.eventType,
                          confirmLabel: interruptData.payload.confirmLabel,
                          cancelLabel: interruptData.payload.cancelLabel,
                          message: interruptData.payload.message,
                          question: interruptData.payload.question,
                          options: interruptData.payload.options,
                          allowMultiple: interruptData.payload.allowMultiple,
                          namespace: interruptData.payload.namespace,
                          confirmationType: interruptData.payload.confirmationType,
                          steps: interruptData.payload.steps,
                          metadata: interruptData.payload.metadata,
                        }),
                      );
                      outcome = 'interrupt';
                      console.debug('📡 [SSE] RUN_FINISHED with interrupt:', {
                        interruptId: interruptData.id,
                        eventType: interruptData.payload.eventType,
                      });
                    } else {
                      // Normal completion
                      dispatch(completeWorkflow());
                      outcome = 'success';

                      // Invalidate task cache to trigger refetch
                      dispatch(
                        ruleBuildApi.util.invalidateTags([{ type: 'Task', id: taskId }]),
                      );
                    }

                    updateCachedData(draft => {
                      draft.isActive = false;
                      draft.isDone = true;
                    });
                    streamEndTime = Date.now();
                    dispatchPerformanceMetrics();
                    break;
                  }

                  case 'TOOL_CALL_START': {
                    const toolCallId = 'toolCallId' in data ? data.toolCallId : crypto.randomUUID();
                    const toolCallName = 'toolCallName' in data ? data.toolCallName : 'unknown';
                    const args = 'args' in data ? JSON.stringify(data.args) : '{}';

                    toolCallMap.set(toolCallId, { toolCallName, args });
                    break;
                  }

                  case 'TOOL_CALL_END': {
                    const toolCallId = 'toolCallId' in data ? data.toolCallId : '';
                    if (toolCallId) {
                      const toolCall = toolCallMap.get(toolCallId);
                      if (toolCall) {
                        console.debug('📡 [AG-UI] Tool call completed:', {
                          toolCallId,
                          toolCallName: toolCall.toolCallName,
                        });
                        toolCallMap.delete(toolCallId);
                      }
                    }
                    break;
                  }

                  case 'ENTITY_AFFECTED': {
                    const entityId = (data as { entityId?: string }).entityId || '';
                    if (entityId) {
                      dispatch(markEntityAffected(entityId));
                    }
                    break;
                  }

                  default:
                    console.debug('📡 [SSE] Unhandled event type:', (data as { type: string }).type);
                }
              },

              onerror: err => {
                console.error('📡 [SSE] Stream error:', err);
                dispatch(setError('Connection error. Please try again.'));
                outcome = 'error';
                streamEndTime = Date.now();
                dispatchPerformanceMetrics();

                updateCachedData(draft => {
                  draft.isActive = false;
                  draft.isDone = true;
                });

                throw err;
              },
            });
          } catch (error) {
            console.error('📡 [SSE] Stream failed:', error);
            dispatch(setError('Failed to connect to chat service. Please try again.'));
            outcome = 'error';
            streamEndTime = Date.now();
            dispatchPerformanceMetrics();

            updateCachedData(draft => {
              draft.isActive = false;
              draft.isDone = true;
            });
          }
        })();

        await cacheEntryRemoved;
        controller.abort();
      },
    }),
  }),
});

export const { useStreamRuleBuildChatQuery } = ruleBuildStreamingApi;
